import create from "zustand";
import { isRejectedGrant, renewAccessToken, TokenResponse } from "@/api/oauthApi";
import { AuthMethod, clearLastLoginMethod, recordOAuthLogin } from "@/auth/authMethod";

export enum OAuthSessionStatus {
    INITIALIZING = "initializing",
    EXCHANGING_CODE = "exchanging_code",
    AUTHENTICATED = "authenticated",
    SIGNED_OUT = "signed_out",
    FAILED = "failed",
}

export enum EndSessionReason {
    RENEWAL_FAILED = "renewal_failed",
    LOGOUT = "logout",
}

const AUTH_TOKEN_RENEWAL_LIFETIME_FRACTION = 0.8;

export interface AuthStoreState {
    method: AuthMethod;
    status: OAuthSessionStatus;

    /** Read with `getState()`. Never select it: every renewal would re-render each subscriber. */
    accessToken: string | null;
    expiresAt: number | null;

    setMethod: (method: AuthMethod) => void;
    setStatus: (status: OAuthSessionStatus) => void;
    setSession: (token: TokenResponse) => void;

    /** Resolves to false when the renewal failed and the session ended. */
    renew: () => Promise<boolean>;

    endSession: (reason: EndSessionReason) => void;
}

let renewalTimer: ReturnType<typeof setTimeout> | undefined;
let inFlightRenewal: Promise<boolean> | undefined;

// Bumped every time a session ends, so a renewal can tell whether the session it was renewing is
// still the current one by the time it answers.
let currentSessionId = 0;

const clearRenewalTimer = () => {
    if (renewalTimer) {
        clearTimeout(renewalTimer);
        renewalTimer = undefined;
    }
};

export const useAuthStore = create<AuthStoreState>((set, get) => ({
    method: AuthMethod.API_KEY,
    status: OAuthSessionStatus.INITIALIZING,
    accessToken: null,
    expiresAt: null,

    setMethod(method) {
        set({ method });
    },

    setStatus(status) {
        set({ status });
    },

    setSession({ accessToken, expiresIn }) {
        clearRenewalTimer();

        // Renewal fires early by a random few minutes so tabs that booted together stop renewing in step.
        const delayMs =
            Math.max(1, Math.floor(expiresIn * AUTH_TOKEN_RENEWAL_LIFETIME_FRACTION - Math.random() * 300)) * 1000;
        renewalTimer = setTimeout(() => {
            void get().renew();
        }, delayMs);

        recordOAuthLogin();

        set({ accessToken, expiresAt: Date.now() + expiresIn * 1000, status: OAuthSessionStatus.AUTHENTICATED });
    },

    async renew() {
        const renewingSessionId = currentSessionId;

        inFlightRenewal ??= renewAccessToken()
            .then((token) => {
                if (renewingSessionId !== currentSessionId) {
                    return false;
                }

                get().setSession(token);
                return true;
            })
            .catch((error: unknown) => {
                if (renewingSessionId === currentSessionId && isRejectedGrant(error)) {
                    get().endSession(EndSessionReason.RENEWAL_FAILED);
                }

                return false;
            })
            .finally(() => {
                inFlightRenewal = undefined;
            });

        return inFlightRenewal;
    },

    endSession() {
        currentSessionId += 1;
        inFlightRenewal = undefined;
        clearLastLoginMethod();
        clearRenewalTimer();
        set({ accessToken: null, expiresAt: null, status: OAuthSessionStatus.SIGNED_OUT });
    },
}));

export const getAccessToken = () => useAuthStore.getState().accessToken;

export const isOAuthSession = () => useAuthStore.getState().method === AuthMethod.OAUTH;
